/**
 * Frontend human-in-the-loop tool for multi-step question flows.
 *
 * Registered via makeAssistantTool so assistant-ui:
 *   1. Sends the tool definition in RunAgentInput.tools
 *   2. Sets status to "requires-action" for human input
 *   3. Enables addResult() to send the user's answers back
 *
 * Supports text input, single-choice, and multi-choice question types
 * with stepper-style navigation between steps.
 */
import { useState, useMemo, useCallback } from "react";
import { makeAssistantTool } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { ToolCard } from "./ToolCard";

interface QuestionDef {
  id: string;
  question: string;
  type: "text" | "single_choice" | "multi_choice";
  options?: string[];
  placeholder?: string;
}

interface QuestionFlowArgs {
  title: string;
  description: string;
  questions: string;
}

function parseQuestions(raw: unknown): QuestionDef[] | null {
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (item): item is QuestionDef =>
        typeof item === "object" &&
        item !== null &&
        typeof item.id === "string" &&
        typeof item.question === "string" &&
        typeof item.type === "string"
    );
  } catch {
    return null;
  }
}

// -- Individual question renderers --

interface QuestionProps {
  question: QuestionDef;
  value: string | string[];
  onChange: (value: string | string[]) => void;
}

function TextQuestion({ question, value, onChange }: QuestionProps) {
  return (
    <textarea
      value={typeof value === "string" ? value : ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={question.placeholder ?? "Type your answer..."}
      rows={3}
      className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none"
    />
  );
}

function SingleChoiceQuestion({ question, value, onChange }: QuestionProps) {
  const selected = typeof value === "string" ? value : "";
  return (
    <div className="flex flex-col gap-1.5">
      {question.options?.map((opt) => {
        const isSelected = selected === opt;
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(opt)}
            className={cn(
              "flex items-center text-left text-sm rounded-lg border px-3 py-2.5 transition-all duration-150",
              isSelected
                ? "border-amber-500 bg-amber-100 text-amber-800 font-medium"
                : "border-amber-200 bg-amber-50/60 text-gray-800 hover:bg-amber-100 hover:border-amber-300 active:scale-[0.98] cursor-pointer"
            )}
          >
            <div
              className={cn(
                "size-4 rounded-full border mr-2.5 shrink-0 flex items-center justify-center",
                isSelected ? "bg-amber-600 border-amber-600" : "border-gray-300 bg-white"
              )}
            >
              {isSelected && <Check className="size-3 text-white" />}
            </div>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function MultiChoiceQuestion({ question, value, onChange }: QuestionProps) {
  const selected = new Set(Array.isArray(value) ? value : []);

  const toggle = (opt: string) => {
    const next = new Set(selected);
    if (next.has(opt)) {
      next.delete(opt);
    } else {
      next.add(opt);
    }
    onChange([...next]);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {question.options?.map((opt) => {
        const isSelected = selected.has(opt);
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={isSelected}
            onClick={() => toggle(opt)}
            className={cn(
              "flex items-center text-left text-sm rounded-lg border px-3 py-2.5 transition-all duration-150",
              isSelected
                ? "border-amber-500 bg-amber-100 text-amber-800 font-medium"
                : "border-amber-200 bg-amber-50/60 text-gray-800 hover:bg-amber-100 hover:border-amber-300 active:scale-[0.98] cursor-pointer"
            )}
          >
            <div
              className={cn(
                "size-4 rounded border mr-2.5 shrink-0 flex items-center justify-center",
                isSelected ? "bg-amber-600 border-amber-600" : "border-gray-300 bg-white"
              )}
            >
              {isSelected && <Check className="size-3 text-white" />}
            </div>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// -- Progress dots --

function ProgressDots({
  total,
  current,
  className,
}: {
  total: number;
  current: number;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={cn(
            "size-2 rounded-full transition-colors",
            i < current ? "bg-amber-400" : i === current ? "bg-amber-600" : "bg-gray-200"
          )}
        />
      ))}
      <span className="text-xs text-muted-foreground ml-1.5">
        {current + 1} of {total}
      </span>
    </div>
  );
}

// -- Main render --

const QuestionFlowRender: ToolCallMessagePartComponent<QuestionFlowArgs, string> = ({
  args,
  result,
  status,
  addResult,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});

  const questions = useMemo(() => (args ? parseQuestions(args.questions) : null), [args]);

  const isRunning = status.type === "running";
  const isSubmitted = result != null;
  const totalSteps = questions?.length ?? 0;
  const currentQuestion = questions?.[currentStep];

  const currentAnswer = currentQuestion ? (answers[currentQuestion.id] ?? "") : "";
  const isCurrentAnswered =
    currentQuestion?.type === "multi_choice"
      ? Array.isArray(currentAnswer) && currentAnswer.length > 0
      : typeof currentAnswer === "string" && currentAnswer.length > 0;

  const handleAnswerChange = useCallback(
    (value: string | string[]) => {
      if (!currentQuestion) return;
      setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
    },
    [currentQuestion]
  );

  const handleSubmit = () => {
    if (!questions) return;
    const parts = questions.map((q) => {
      const answer = answers[q.id];
      const formatted = Array.isArray(answer) ? answer.join(", ") : (answer ?? "");
      return `${q.question}: ${formatted}`;
    });
    addResult(
      `The user answered: ${parts.join(" | ")}. Respond with text only. Do NOT call any tool.`
    );
  };

  // Submitted state — collapsed summary
  if (isSubmitted && questions) {
    return (
      <ToolCard
        label="Question Flow"
        color="amber"
        isLoading={false}
        loadingText=""
        isSubmitted={true}
      >
        {args?.title && <p className="font-medium text-sm">{args.title}</p>}
        <div className="flex flex-col gap-2">
          {questions.map((q) => {
            const answer = answers[q.id];
            const formatted = Array.isArray(answer) ? answer.join(", ") : (answer ?? "");
            return (
              <div key={q.id} className="text-sm">
                <p className="text-muted-foreground text-xs">{q.question}</p>
                <p className="font-medium">{formatted}</p>
              </div>
            );
          })}
        </div>
      </ToolCard>
    );
  }

  return (
    <ToolCard
      label="Question Flow"
      color="amber"
      isLoading={isRunning || !questions || !currentQuestion}
      loadingText="Generating questions..."
      isSubmitted={false}
    >
      {args?.title && <p className="font-medium text-sm">{args.title}</p>}
      {args?.description && currentStep === 0 && (
        <p className="text-xs text-muted-foreground">{args.description}</p>
      )}

      <ProgressDots total={totalSteps} current={currentStep} />

      {currentQuestion && (
        <div className="space-y-2">
          <p className="text-sm font-medium">{currentQuestion.question}</p>

          {currentQuestion.type === "text" && (
            <TextQuestion
              question={currentQuestion}
              value={currentAnswer}
              onChange={handleAnswerChange}
            />
          )}
          {currentQuestion.type === "single_choice" && (
            <SingleChoiceQuestion
              question={currentQuestion}
              value={currentAnswer}
              onChange={handleAnswerChange}
            />
          )}
          {currentQuestion.type === "multi_choice" && (
            <MultiChoiceQuestion
              question={currentQuestion}
              value={currentAnswer}
              onChange={handleAnswerChange}
            />
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-1">
        <Button
          variant="ghost"
          size="sm"
          disabled={currentStep === 0}
          onClick={() => setCurrentStep((s) => s - 1)}
        >
          <ChevronLeft className="size-4 mr-1" />
          Back
        </Button>

        {currentStep < totalSteps - 1 ? (
          <Button
            size="sm"
            disabled={!isCurrentAnswered}
            onClick={() => setCurrentStep((s) => s + 1)}
          >
            Next
            <ChevronRight className="size-4 ml-1" />
          </Button>
        ) : (
          <Button size="sm" disabled={!isCurrentAnswered} onClick={handleSubmit}>
            Submit
          </Button>
        )}
      </div>
    </ToolCard>
  );
};

// Intersection with Record<string, unknown> needed because makeAssistantTool's
// generic constraint requires an index signature that interfaces don't provide.
export const QuestionFlowTool = makeAssistantTool<
  QuestionFlowArgs & Record<string, unknown>,
  string
>({
  toolName: "ask_question_flow",
  type: "human",
  description:
    "Present a multi-step question flow to gather structured input from the user. " +
    "Supports text input, single-choice, and multi-choice questions with step-by-step navigation.",
  parameters: {
    type: "object",
    properties: {
      title: { type: "string", description: "Overall title for the question flow" },
      description: {
        type: "string",
        description: "Context about what this flow is gathering input for",
      },
      questions: {
        type: "string",
        description:
          'JSON array of question objects, each with: id (string), question (string), type ("text" | "single_choice" | "multi_choice"), options (string array, required for choice types), placeholder (optional string for text type). Example: [{"id":"q1","question":"What is your goal?","type":"text","placeholder":"Describe..."},{"id":"q2","question":"Pick a method","type":"single_choice","options":["Method A","Method B"]}]',
      },
    },
    required: ["title", "questions"],
  },
  render: QuestionFlowRender,
});
